import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { NotificationReason, NotificationReasonId } from '@ninety/platform/models/notifications/notification-reason';
import { ErrorService } from '@ninety/platform/services';
import { environment } from '@ninety/ui/web/environments';

import {
  BackendNotificationSettings,
  CHANNEL_IDS,
  FeedNotification,
  NOTIFICATION_CATEGORIES,
  NotificationChannel,
} from '../models/notification-settings.model';

type NotificationReasonChannelUpdate = Pick<BackendNotificationSettings, 'channel_id' | 'reason_id' | 'is_enabled'>;

@Injectable({
  providedIn: 'root',
})
export class NotificationsSettingsService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);

  private readonly apiUrl = `${environment.notificationsApiOrigin}/v1/notifications/settings`;
  private readonly reasonChannelApiUrl = `${this.apiUrl}/reason-channel`;
  private readonly defaultChannels: NotificationChannel = { email: false, web: false, mobile: false };

  getNotificationSettings(throwOnError = false): Observable<FeedNotification[]> {
    return this.http.get<BackendNotificationSettings[]>(this.apiUrl).pipe(
      map(backendSettings => this.mapBackendToFrontend(backendSettings)),
      catchError((error: unknown) => {
        if (throwOnError) {
          return throwError(() => error);
        }
        return of(this.getDefaultSettings());
      })
    );
  }

  /**
   * Updates a single notification reason + channel.
   */
  updateNotificationChannel(
    reason: NotificationReason,
    channel: keyof NotificationChannel,
    isEnabled: boolean
  ): Observable<void> {
    const category = this.findCategoryByReason(reason);
    const channelId = CHANNEL_IDS[channel];

    if (!category || channelId === undefined) {
      return of(void 0);
    }

    return this.upsertReasonChannelSettings(this.buildReasonChannelUpdates(category.reasonIds, channelId, isEnabled));
  }

  /**
   * Bulk update for a given channel across many notification reasons.
   */
  updateNotificationsForChannel(
    reasons: NotificationReason[],
    channel: keyof NotificationChannel,
    isEnabled: boolean
  ): Observable<void> {
    if (!Array.isArray(reasons) || reasons.length === 0) {
      return of(void 0);
    }

    const channelId = CHANNEL_IDS[channel];
    if (channelId === undefined) {
      return of(void 0);
    }

    const updates: NotificationReasonChannelUpdate[] = [];

    for (const reason of reasons) {
      const category = this.findCategoryByReason(reason);
      if (!category) {
        continue;
      }
      updates.push(...this.buildReasonChannelUpdates(category.reasonIds, channelId, isEnabled));
    }

    return this.upsertReasonChannelSettings(updates);
  }

  /**
   * Legacy/compat: upserts all categories/channels from feed settings.
   */
  updateNotificationSettings(settings: FeedNotification[]): Observable<void> {
    const updates = this.mapFrontendToReasonChannelUpdates(settings);
    return this.upsertReasonChannelSettings(updates);
  }

  private upsertReasonChannelSettings(updates: NotificationReasonChannelUpdate[]): Observable<void> {
    if (updates.length === 0) {
      return of(void 0);
    }
    return this.http.patch<BackendNotificationSettings[]>(this.reasonChannelApiUrl, updates).pipe(
      map(() => void 0),
      catchError((e: unknown) =>
        this.errorService.notify(e, 'Could not update notification settings. Please try again.')
      )
    );
  }

  private getDefaultSettings(): FeedNotification[] {
    return NOTIFICATION_CATEGORIES.map(category => ({
      reason: category.reason,
      name: category.name,
      description: category.description,
      channels: { ...this.defaultChannels },
      hideEmail: category.hideEmail,
    }));
  }

  private mapBackendToFrontend(backendSettings: BackendNotificationSettings[]): FeedNotification[] {
    const settingsByReasonId = new Map<number, BackendNotificationSettings[]>();
    for (const setting of backendSettings) {
      const group = settingsByReasonId.get(setting.reason_id);
      if (group) {
        group.push(setting);
      } else {
        settingsByReasonId.set(setting.reason_id, [setting]);
      }
    }

    return NOTIFICATION_CATEGORIES.map(category => {
      const channels: NotificationChannel = { ...this.defaultChannels };
      // A channel shows as enabled if any of the category's reasons is enabled, so the UI reflects what the user receives
      const categorySettings = category.reasonIds.flatMap(reasonId => settingsByReasonId.get(reasonId) ?? []);
      categorySettings.forEach(setting => this.applyBackendSettingToChannels(channels, setting));

      return {
        reason: category.reason,
        name: category.name,
        description: category.description,
        channels,
        hideEmail: category.hideEmail,
      };
    });
  }

  private mapFrontendToReasonChannelUpdates(settings: FeedNotification[]): NotificationReasonChannelUpdate[] {
    const updates: NotificationReasonChannelUpdate[] = [];
    const channelKeys: Array<keyof NotificationChannel> = ['email', 'web', 'mobile'];

    settings.forEach(notification => {
      const category = this.findCategoryByReason(notification.reason);
      if (!category) return;

      channelKeys.forEach(channelName => {
        const isEnabled = notification.channels[channelName];
        const channelId = CHANNEL_IDS[channelName];
        if (channelId !== undefined) {
          updates.push(...this.buildReasonChannelUpdates(category.reasonIds, channelId, isEnabled));
        }
      });
    });

    return updates;
  }

  private findCategoryByReason(reason: NotificationReason) {
    return NOTIFICATION_CATEGORIES.find(category => category.reason === reason);
  }

  /** One update per backend reason the category toggles. */
  private buildReasonChannelUpdates(
    reasonIds: readonly NotificationReasonId[],
    channelId: number,
    isEnabled: boolean
  ): NotificationReasonChannelUpdate[] {
    return reasonIds.map(id => ({
      channel_id: channelId,
      reason_id: id,
      is_enabled: isEnabled,
    }));
  }

  private applyBackendSettingToChannels(channels: NotificationChannel, setting: BackendNotificationSettings): void {
    if (setting.channel_id === CHANNEL_IDS.email) {
      channels.email ||= setting.is_enabled;
    } else if (setting.channel_id === CHANNEL_IDS.web) {
      channels.web ||= setting.is_enabled;
    } else if (setting.channel_id === CHANNEL_IDS.mobile) {
      channels.mobile ||= setting.is_enabled;
    }
  }
}
